import {isBefore} from 'date-fns';
import {openDialog, closeDialog, Hyperlink, AlertProps} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {
  useGetServiceCaseIntegrationsQuery,
  useGetServiceCaseIntegrationsXentrySynchronizationStatusQuery,
  usePostAvailableXentryOrdersMutation,
  usePostIntegrationsForXentryLinkMutation,
  usePostServiceCaseIntegrationsXentryOrderManualCreationMutation,
  usePostServiceCaseIntegrationsXentryOrderSynchronizationApiMutation,
  XentryIntegrationAction,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, EMPTY_PLACEHOLDER, LinkXentry, notificationTypes} from '@dms/shared';

import {Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ConfirmChangeXentryOrderDialog} from '../components/ConfirmChangeXentryOrderDialog';
import {ConfirmUnlinkDialog} from '../components/ConfirmUnlinkDialog';
import {SyncXentry} from '../components/SyncXentry/SyncXentry';
import {DAIMLER_INTEGRATION_CODE} from '../constants/daimlerIntegrationCode';
import {XENTRY_INTEGRATION_CODE} from '../constants/xentryIntegrationCode';

interface XentryIntegrationProps extends RequiredTestIdProps {
  serviceCaseId: string;
}

export function useXentryIntegration(props: XentryIntegrationProps) {
  'use no memo';
  const [firstXentrySynchronizationAt, setFirstXentrySynchronizationAt] = useState<string | null>(
    null
  );
  const {data: integrations, refetch: refetchIntegrations} = useGetServiceCaseIntegrationsQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNilOrEmpty(props.serviceCaseId)}
  );

  const hasXentryIntegration = integrations?.caseIntegration?.some(
    (caseIntegration) =>
      caseIntegration?.integration?.code === XENTRY_INTEGRATION_CODE ||
      caseIntegration?.integration?.code === DAIMLER_INTEGRATION_CODE
  );

  const {data: xentrySynchronization, refetch: refetchXentrySynchronization} =
    useGetServiceCaseIntegrationsXentrySynchronizationStatusQuery(
      {
        serviceCaseId: props.serviceCaseId,
      },
      {skip: !hasXentryIntegration}
    );
  const [postAvailableXentryOrders] = usePostAvailableXentryOrdersMutation({
    fixedCacheKey: 'open-link-xentry',
  });
  const [postIntegrationsForXentryLink] = usePostIntegrationsForXentryLinkMutation();
  const [postServiceCaseIntegrationsXentryOrderManualCreation] =
    usePostServiceCaseIntegrationsXentryOrderManualCreationMutation();
  const [postXentrySync] = usePostServiceCaseIntegrationsXentryOrderSynchronizationApiMutation();

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type === notificationTypes.INTEGRATED_SERVICE_CHANGE) {
      refetchIntegrations();
      refetchXentrySynchronization();
    }
  });

  useEffect(
    function storeFirstXentrySynchronizationAt() {
      if (xentrySynchronization) {
        setFirstXentrySynchronizationAt(xentrySynchronization.lastUpdateFromXentryAt ?? null);
      }
    },
    [xentrySynchronization?.synchronizeAfterOpening] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  useEffect(
    function syncXentryAfterOpening() {
      if (xentrySynchronization?.synchronizeAfterOpening) {
        postXentrySync({
          serviceCaseId: props.serviceCaseId,
        })
          .unwrap()
          .then((data) => {
            if (!data) {
              refetchIntegrations();
              return;
            }

            openDialog(
              <SyncXentry
                serviceCaseId={props.serviceCaseId}
                groups={data.groups ?? []}
                data-testid={suffixTestId('syncXentry', props)}
              />,
              {
                title: i18n.t('entity.integration.labels.xentrySync'),
                id: 'selectXentryDialog',
                size: 'large',
              }
            );
          })
          .catch(handleApiError);
      }
      // We should do this change only once, when data is loaded
    },
    [!!xentrySynchronization] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const handleLinkXetry = (xentryOrderId: string) =>
    postIntegrationsForXentryLink({serviceCaseId: props.serviceCaseId, body: {xentryOrderId}})
      .unwrap()
      .catch(handleApiError);

  const handleCreateNewXetry = () =>
    postServiceCaseIntegrationsXentryOrderManualCreation({
      serviceCaseId: props.serviceCaseId,
    })
      .unwrap()
      .catch(handleApiError);

  const openLinkXentryDialog = () => {
    postAvailableXentryOrders({serviceCaseId: props.serviceCaseId}).unwrap();

    openDialog(
      <LinkXentry
        serviceCaseId={props.serviceCaseId}
        onClose={() => closeDialog('selectXentryDialog')}
        onLink={(xentryOrderId) =>
          handleLinkXetry(xentryOrderId).then(() => closeDialog('selectXentryDialog'))
        }
        onLinkNew={() => handleCreateNewXetry().then(() => closeDialog('selectXentryDialog'))}
        data-testid={suffixTestId('linkXentry', props)}
      />,
      {title: i18n.t('entity.integration.labels.selectXentryOrder'), id: 'selectXentryDialog'}
    );
  };

  const openUnlinkConfirm = () =>
    openDialog(
      <ConfirmUnlinkDialog
        dialogId="confirmUnlinkXentryOrder"
        serviceCaseId={props.serviceCaseId}
        xentryId={xentrySynchronization?.xentryJobData?.externalId}
        data-testid={suffixTestId('unlinkXentry', props)}
      />,
      {
        id: 'confirmUnlinkXentryOrder',
        size: 'small',
      }
    );

  const openChangeConfirm = (xentryId: string | Nullish, onConfirm: () => unknown) =>
    openDialog(
      <ConfirmChangeXentryOrderDialog
        dialogId="confirmChangeXentryOrder"
        serviceCaseId={props.serviceCaseId}
        xentryId={xentryId}
        onConfirm={onConfirm}
        data-testid={suffixTestId('unlinkXentry', props)}
      />,
      {
        id: 'confirmChangeXentryOrder',
        size: 'small',
      }
    );

  const openChangeXentryDialog = () => {
    postAvailableXentryOrders({serviceCaseId: props.serviceCaseId}).unwrap();

    openDialog(
      <LinkXentry
        serviceCaseId={props.serviceCaseId}
        linkedXentryId={xentrySynchronization?.xentryJobData?.externalId}
        onClose={() => closeDialog('changeXentryDialog')}
        onLink={(xentryOrderId) => {
          openChangeConfirm(xentryOrderId, () => {
            handleLinkXetry(xentryOrderId);
            closeDialog('changeXentryDialog');
          });
        }}
        onLinkNew={() => {
          openChangeConfirm(null, () => {
            handleCreateNewXetry();
            closeDialog('changeXentryDialog');
          });
        }}
        data-testid={suffixTestId('linkXentry', props)}
      />,
      {title: i18n.t('entity.integration.labels.selectXentryOrder'), id: 'changeXentryDialog'}
    );
  };

  const getAction = (action: XentryIntegrationAction | Nullish) => {
    const baseAction = {isDisabled: action?.style === 'disabled', size: 'small' as const};

    return match<string | Nullish, Hyperlink>(action?.key)
      .with('xentry-job-link', () => ({
        ...baseAction,
        title: i18n.t('entity.integration.actions.link'),
        leftIcon: 'content/link',
        onClick: openLinkXentryDialog,
      }))
      .with('xentry-job-unlink', () => ({
        ...baseAction,
        title: i18n.t('entity.integration.actions.unlink'),
        leftIcon: 'content/link_off',
        onClick: openUnlinkConfirm,
      }))
      .with('xentry-job-change', () => ({
        ...baseAction,
        title: i18n.t('entity.integration.actions.unlinkAndChange'),
        leftIcon: 'content/link_off',
        onClick: openChangeXentryDialog,
      }))
      .with('xentry-job-hyperlink', () => ({
        ...baseAction,
        title: i18n.t('entity.integration.actions.viewOrder'),
        leftIcon: 'action/open_in_new',
        onClick: () => window.open(xentrySynchronization?.xentryJobData?.viewDetailsLink ?? ''),
      }))
      .with('xentry-job-sync', () => ({
        ...baseAction,
        title: i18n.t('entity.orderIntegration.actions.sync'),
        leftIcon: 'action/autorenew',
        onClick: () =>
          postXentrySync({
            serviceCaseId: props.serviceCaseId,
          })
            .unwrap()
            .then((data) => {
              if (!data) {
                refetchIntegrations();
                return;
              }

              openDialog(
                <SyncXentry
                  serviceCaseId={props.serviceCaseId}
                  groups={data.groups ?? []}
                  data-testid={suffixTestId('syncXentry', props)}
                />,
                {
                  title: i18n.t('entity.integration.labels.xentrySync'),
                  id: 'selectXentryDialog',
                  size: 'large',
                }
              );
            })
            .catch(handleApiError),
      }))
      .otherwise(() => ({title: EMPTY_PLACEHOLDER}));
  };

  const hyperlinks: Hyperlink[] | undefined = xentrySynchronization?.actions
    ?.filter((action) => action?.style !== 'hidden')
    .map(getAction);

  const xentrySynchronizationStatus = xentrySynchronization?.integrationStatus;

  const shouldDisplayRefreshBanner = match([
    firstXentrySynchronizationAt,
    xentrySynchronization?.lastUpdateFromXentryAt,
  ])
    .with([null, Pattern.string], () => true)
    .with([Pattern.string, Pattern.string], ([first, last]) =>
      isBefore(parseDate(first), parseDate(last))
    )
    .otherwise(() => false);

  const xentryAlert =
    hasXentryIntegration &&
    match<[typeof xentrySynchronizationStatus, boolean], AlertProps | undefined>([
      xentrySynchronizationStatus,
      shouldDisplayRefreshBanner,
    ])
      .with([Pattern.any, true], () => ({
        title: i18n.t('entity.integration.labels.newVersionOfServiceCase'),
        message: i18n.t('entity.integration.labels.pleaseRefresh'),
        variant: 'warning',
        hyperlinks: [
          ...(hyperlinks ?? []),
          {
            title: i18n.t('general.actions.refresh'),
            leftIcon: 'navigation/refresh',
            onClick: () => window.location.reload(),
          },
        ],
      }))
      .with(['LINKED', Pattern.boolean], () => ({
        title: i18n.t('entity.integration.labels.successLinkedXentry', {
          xentryId: xentrySynchronization?.xentryJobData?.externalId,
        }),
        variant: 'success',
        hyperlinks,
      }))
      .with(['UNLINKED', Pattern.boolean], () => ({
        title: i18n.t('entity.integration.labels.warningNotLinkedXentry'),
        variant: 'warning',
        hyperlinks,
      }))
      .with(['PENDING', Pattern.boolean], () => ({
        title: i18n.t('entity.integration.labels.pendingSynchronization', {
          xentryId: xentrySynchronization?.xentryJobData?.externalId,
        }),
        message: match(xentrySynchronization?.pendingReason)
          .with('MISSING_VIN', () => i18n.t('entity.integration.labels.missingVin'))
          .with('INVALID_VIN', () => i18n.t('entity.integration.labels.invalidVin'))
          .otherwise(() => i18n.t('entity.integration.labels.pleaseSynchronizeManually')),
        variant: 'error',
        hyperlinks,
      }))

      .otherwise(() => undefined);

  return {xentryAlert};
}
